import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@editor/trpc/nextJSProject";

import { useUpdatePageFilesInWebContainer } from "@/features/code-execution/web-container/useUpdatePageFilesInWebContainer";
import { successToast } from "@replo/design-system/components/alert/Toast";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Menu } from "@replo/design-system/components/menu/Menu";
import { Copy, Ellipsis, ExternalLink, Pencil, Trash } from "lucide-react";

export default function PageCardMenu({
  page,
  isPublished,
  publishedSiteUrl,
  setIsRenamingPage,
}: {
  page: FileWithLatestFileVersion;
  isPublished: boolean;
  publishedSiteUrl?: string;
  setIsRenamingPage: (isRenamingPage: boolean) => void;
}) {
  const { openModal } = useModal();
  const projectId = useCurrentProjectId();
  const updatePageFilesInWebContainer = useUpdatePageFilesInWebContainer();

  const { mutate: duplicatePage, isPending: isDuplicating } =
    nextJSProjectRouter.duplicatePage.useMutation({
      onSuccess: async ({ createdFile }) => {
        void nextJSProjectUtils.listPages.invalidate({ projectId });
        await updatePageFilesInWebContainer({
          pages: [{ id: createdFile.id }],
        });
        successToast("Page duplicated", `"${createdFile.name}" was created`);
      },
    });

  return (
    <Menu
      items={[
        {
          id: "rename",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: "Rename",
          startEnhancer: <Pencil size={16} />,
          onSelect: () => {
            setIsRenamingPage(true);
          },
        },
        {
          id: "duplicate",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: "Duplicate",
          startEnhancer: <Copy size={16} />,
          disabled: isDuplicating,
          onSelect: () => {
            if (projectId) {
              duplicatePage({
                projectId,
                fileId: page.id,
              });
            }
          },
        },
        ...(isPublished && publishedSiteUrl
          ? [
              {
                id: "view-live",
                type: "leaf" as const,
                variant: "default" as const,
                size: "sm" as const,
                title: "View live",
                startEnhancer: <ExternalLink size={16} />,
                onSelect: () => {
                  window.open(publishedSiteUrl, "_blank");
                },
              },
            ]
          : []),
        {
          id: "delete",
          type: "leaf",
          variant: "danger",
          size: "sm",
          title: "Delete",
          startEnhancer: <Trash size={16} />,
          onSelect: () => {
            openModal({
              type: "deletePageModal",
              props: {
                page,
              },
            });
          },
        },
      ]}
      align="start"
      side="bottom"
      trigger={
        <IconButton
          variant="secondary"
          size="sm"
          icon={<Ellipsis size={16} className="text-muted" />}
        />
      }
    />
  );
}
